import * as React from 'react';
import * as styles from './AssetIconAndInput.scss';
import { AssetTextFieldProps, default as AssetTextField } from './AssetTextField';
import { observer } from 'mobx-react-lite';
import { useCommitIfValueChanged } from 'owa-react-hooks/lib/useCommitIfValueChanged';
import { useCommitOnEnterRevertOnEscape } from 'owa-react-hooks/lib/useCommitOnEnterRevertOnEscape';
import { useOnBlurUpdate } from 'owa-react-hooks/lib/useOnBlurUpdate';
import { useTextChangeCallback } from 'owa-react-hooks/lib/useTextChangeCallback';
import AssetIcon from './AssetIcon';
import loc from 'owa-localize';

export default observer(function AssetIconAndInput({
    customIcon,
    iconName,
    placeholderResourceId,
    initialValue,
    currentValue,
    setCurrentValue,
    onCommit,
    multiline,
    textFieldProps,
}: {
    customIcon?: JSX.Element | null | false;
    iconName: string;
    placeholderResourceId: string;
    initialValue: string | undefined;
    currentValue: string;
    setCurrentValue: (newValue: string) => void;
    onCommit: (newValue: string | undefined) => void;
    multiline: boolean;
    textFieldProps?: Exclude<
        AssetTextFieldProps,
        | 'className'
        | 'placeholder'
        | 'value'
        | 'onChange'
        | 'onBlur'
        | 'onKeyDown'
        | 'multiline'
        | 'autoAdjustHeight'
        | 'resizable'
    >;
}) {
    const onBlur = useOnBlurUpdate(useCommitIfValueChanged(initialValue, currentValue, onCommit));

    const onRevert = React.useCallback(
        (oldValue: string | undefined) => {
            setCurrentValue(oldValue || '');
        },
        [setCurrentValue]
    );

    const onKeyDown = useCommitOnEnterRevertOnEscape(
        initialValue,
        currentValue,
        onCommit,
        onRevert
    );

    const onChange = useTextChangeCallback(setCurrentValue);

    return (
        <div className={styles.assetIconAndInput}>
            {customIcon}
            {!customIcon && <AssetIcon className={styles.icon} iconName={iconName} />}
            <AssetTextField
                {...textFieldProps}
                className={styles.input}
                placeholder={loc(placeholderResourceId)}
                value={currentValue}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                multiline={multiline}
                autoAdjustHeight={true}
                resizable={false}
            />
        </div>
    );
});
